import React, { useLayoutEffect, useRef, useState } from "react";
import debounce from 'lodash.debounce';
import {useTranslation} from "react-i18next";

export default function SimpleGrid({ columns, dataSource, columnClasses, actions }) {
    const cellRefs = useRef([]);
    let {t} = useTranslation();

    const [originalTexts, setOriginalTexts] = useState({});

    const resetRefs = () => {
        cellRefs.current = [];
    };

    const handleOverflowAndTrim = () => {
        const newOriginalTexts = {};

        dataSource.forEach((row, rowIndex) => {
            row.forEach((cell, cellIndex) => {
                const cellDiv = cellRefs.current[rowIndex]?.[cellIndex];
                if (!cellDiv) return;

                const key = `${rowIndex}-${cellIndex}`;
                newOriginalTexts[key] = cellDiv.innerHTML;

                trimFromLastWord(cellDiv, key);
            });
        });

        setOriginalTexts(newOriginalTexts);
    };

    const debouncedHandleOverflowAndTrim = debounce(handleOverflowAndTrim, 100);

    useLayoutEffect(() => {
        resetRefs();
        requestAnimationFrame(() => {
            handleOverflowAndTrim();
        });

        window.addEventListener("resize", debouncedHandleOverflowAndTrim);
        return () => {
            window.removeEventListener("resize", debouncedHandleOverflowAndTrim);
            debouncedHandleOverflowAndTrim.cancel();
        };
    }, [dataSource]);

    const trimFromLastWord = (cellDiv, key) => {
        const text = originalTexts[key] || cellDiv.innerHTML;
        const words = text.split(' ');

        cellDiv.innerHTML = words[0];
        let height = cellDiv.clientHeight;

        for (let i = 1; i < words.length; i++) {
            const newContent = cellDiv.innerHTML + ' ' + words[i];
            cellDiv.innerHTML = newContent;

            if (cellDiv.clientHeight > height) {
                cellDiv.classList.add('overflowing-text');
                cellDiv.innerHTML = newContent.split(' ').slice(0, -1).join(' ');
                return;
            }

            height = cellDiv.clientHeight;
        }

        cellDiv.innerHTML = text;
        cellDiv.classList.remove('overflowing-text');
    };

    return (
        <div className="w-full overflow-x-auto">
            <table className="comp__simpleGrid table-auto w-full">
                <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/20">
                    {columns.map((col, colIndex) => (
                        <th key={colIndex} className={`text-xs uppercase text-gray-500 dark:text-gray-400 border-t border-b border-gray-100 dark:border-gray-700/60 font-semibold p-2 ${columnClasses?.[colIndex] || ''}`}>
                            {col}
                        </th>
                    ))}
                    {actions && actions.length > 0 && (
                        <th className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold p-2"></th>
                    )}
                </tr>
                </thead>
                <tbody>
                {dataSource.length === 0 ? (
                    <tr>
                        <td colSpan={columns.length + (actions?.length ? 1 : 0)} className="text-center text-gray-400 py-4">
                            {t('no-results')}
                        </td>
                    </tr>
                ) : (
                    dataSource.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-gray-100 dark:border-gray-700/60">
                            {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className={`px-2 py-1 font-medium text-gray-800 dark:text-gray-100 ${columnClasses?.[cellIndex] || ''}`}>
                                    <div
                                        ref={el => {
                                            if (!cellRefs.current[rowIndex]) cellRefs.current[rowIndex] = [];
                                            cellRefs.current[rowIndex][cellIndex] = el;
                                        }}
                                        className="relative inline-flex"
                                        dangerouslySetInnerHTML={{ __html: cell }}
                                    />
                                </td>
                            ))}
                            {actions && actions.length > 0 && (
                                <td className="px-2 py-1 text-right w-1 text-nowrap">
                                    {actions.map((action, actionIndex) => (
                                        <React.Fragment key={actionIndex}>
                                            {typeof action === 'function' ? action(rowIndex, row) : action}
                                        </React.Fragment>
                                    ))}
                                </td>
                            )}
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        </div>
    );
}
